<template>
    <div class="zalo-audio" v-if="!loading">
        <!-- Play / Pause -->
        <div class="play-btn" @click="togglePlay">
            <img v-if="audioPaused" src="@/assets/images/svg/chat/play-voice.svg" alt="play" class="play-icon" />
            <img v-else src="@/assets/images/svg/chat/pause-voice.svg" alt="pause" class="play-icon" />
        </div>

        <!-- Fake Wave -->
        <div class="waveform" ref="waveEl" @click="seek">
            <span v-for="(h, i) in wave" :key="i" class="bar" :class="{ active: i <= activeIndex }" :style="{ height: h + '%' }" />
        </div>

        <!-- Time -->
        <span class="time">{{ timeText }}</span>

        <!-- AUDIO CORE (DUY NHẤT) -->
        <audio
            ref="audioEl"
            :id="id"
            :src="imgUrl"
            preload="metadata"
            @timeupdate="onTimeUpdate"
            @loadedmetadata="onLoaded"
            @ended="onEnded"
            @play="onPlay"
            @pause="onPause"
        />
    </div>

    <div v-else class="zalo-audio loading">Đang tải...</div>
</template>

<script setup>
import { onMounted, defineProps, inject, ref, defineEmits, watch, computed } from "vue";
import isEqual from "lodash/isEqual";
import * as env from "@/config/env";
import { apiRequest } from "../../../services";
import { storageQueryBuilder } from "@/utils/storageQueryBuild";

const injectedCompanyId = inject("$companyId");

const props = defineProps({
    data: { required: true },
    id: {},
    companyId: { type: String, default: "" },
});

const emit = defineEmits(["downloadUrl", "play"]);

/* ===== CORE ===== */
const imgUrl = ref("");
const loading = ref(false);

/* ===== AUDIO STATE ===== */
const audioEl = ref(null);
const currentTime = ref(0);
const duration = ref(0);
const isPlaying = ref(false);
const waveEl = ref(null);
const BAR_WIDTH = 1;
const BAR_GAP = 2; 
/* ===== UI STATE ===== */
const wave = ref([]);
const activeIndex = ref(0);

/* ===== COMPUTED ===== */
const audioPaused = computed(() => !isPlaying.value);

const timeText = computed(() => {
    const remain = duration.value - currentTime.value;
    if (!remain || remain < 0) return "0:00";
    const m = Math.floor(remain / 60);
    const s = Math.floor(remain % 60)
        .toString()
        .padStart(2, "0");
    return `${m}:${s}`;
});

/* ===== UI LOGIC ===== */
function generateWave() {
    if (!waveEl.value) return;

    const width = waveEl.value.clientWidth;
    const count = Math.floor(width / (BAR_WIDTH + BAR_GAP));

    wave.value = Array.from({ length: count }, () =>
        Math.floor(Math.random() * 50 + 30)
    );
}
function togglePlay() {
    const audio = audioEl.value;
    if (!audio) return;

    if (audio.paused) {
        audio.play().then(() => emit("play", audio));
    } else {
        audio.pause();
    }
}

function onLoaded() {
    duration.value = audioEl?.value?.duration;
}

function onTimeUpdate() {
    const audio = audioEl.value;
    if (!audio || !audio.duration) return;

    currentTime.value = audio.currentTime;
    activeIndex.value = Math.floor(
        (audio.currentTime / audio.duration) * wave.value.length
    );
}


function onEnded() {
  isPlaying.value = false;
  currentTime.value = 0;
  activeIndex.value = 0;
}

function seek(e) {
    const audio = audioEl.value;
    if (!audio || !audio.duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;

    audio.currentTime = percent * audio.duration;
    audio.play();
}

/* ===== CORE FETCH (GIỮ NGUYÊN) ===== */
onMounted(() => {
    generateWave();
    getFile();
});

watch(
    () => props.data,
    (n, o) => {
        if (!isEqual(n, o)) getFile();
    }
);

function getFileURl(filePath) {
    return new Promise((resolve, reject) => {
        const bucketId = props.companyId?.length ? props.companyId : injectedCompanyId.value;

        if (!filePath?.startsWith("data:")) {
            const req = storageQueryBuilder("get", bucketId, filePath);
            apiRequest(req.method, req.route).then(resolve).catch(reject);
        }
    });
}

function getFile() {
    loading.value = true;

    if (env.STORAGE_TYPE === "server") {
        getFileURl(props.data).then((res) => {
            imgUrl.value = res.data.url;
            emit("downloadUrl", res.data.url + "&download=true");
            loading.value = false;
        });
        return;
    }

    const cid = props.companyId?.length ? props.companyId : injectedCompanyId.value;

    apiRequest("post", env.WASABI_RETRIVE_OBJECT, {
        companyId: cid,
        path: props.data,
    }).then((res) => {
        if (res.data.status) {
            imgUrl.value = res.data.statusText;
            emit("downloadUrl", res.data.statusText);
        }
        loading.value = false;
    });
}
function onPlay() {
  isPlaying.value = true;
}

function onPause() {
  isPlaying.value = false;
}


</script>

<style>
.zalo-audio {
    display: flex;
    align-items: center;
    background: #FAFAFA;
    border-radius: 18px;
    padding: 6px 10px;
    width: 244px;
    gap: 8px;
    height: 48px;
}

.play-btn {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin-right: 6px;
    font-size: 14px;
}

.waveform {
    display: flex;
    align-items: center;
    flex: 1;
    height: 24px;
    gap: 2px;
    cursor: pointer;
}

.bar {
    width: 1px;
    background: #cfd3da;
    border-radius: 2px;
}

.bar.active {
    background: #141414;
}

.time {
    font-size: 12px;
    margin-left: 6px;
    color: #555;
}

.loading {
    opacity: 0.6;
}
</style>
